import { getUnheldSeatOption, isSeatLocked } from "@components/table/members/assign-seats/assign-seats-utils";
/**
 * How many people can be invited at once while they are given seats. Above that the backend sends
 * the invitations in the background and does not return the members to give the seats to.
 */
export const MAX_INVITED_MEMBERS_WITH_SEATS = 10;

/** What the seats in the invite are for */
export const SEATS_HELP_TEXT = "You can optionally assign seats to members.";

/** Why the selected role cannot be given when no Sphere XG seat is left to give */
export const NO_XG_SEAT_FOR_ROLE_TEXT = "There are no Sphere XG seats left, so nobody can be invited with this role.";
/**
 * Returns the seats to offer the people invited to the workspace, which are the seats of the Manage
 * seats dialog the current user may assign. Nobody invited holds a seat yet, so every seat is one to
 * give, and only a seat the workspace has none of left is locked.
 */
export function getInviteSeatOptions({
  isActiveSeatPlan,
  hasSceneSeats,
  canManageXgSeats,
  canManageSceneLicenses,
  seatsAvailable
}) {
  const seats = [];
  if (isActiveSeatPlan && canManageXgSeats) {
    seats.push("xgSeat");
  }
  if (hasSceneSeats && canManageSceneLicenses) {
    seats.push("sceneSeat");
  }
  return seats.map(seat => getUnheldSeatOption(seat, undefined, seatsAvailable[seat]));
}
/**
 * Returns why the invite cannot be sent with the selected role, or undefined when it can: the role
 * needs a Sphere XG seat and none is left. Too few seats for the people invited is told on the seat
 * card itself, see hasTooFewSeats.
 */
export function getSeatsError({
  seatOptions,
  isXgSeatRequired
}) {
  const xgSeatOption = seatOptions.find(({
    seat
  }) => seat === "xgSeat");
  if (isXgSeatRequired && xgSeatOption && isSeatLocked(xgSeatOption)) {
    return NO_XG_SEAT_FOR_ROLE_TEXT;
  }
  return undefined;
}
/** Whether fewer seats of any kind checked are free than people invited */
export function hasTooFewSeats({
  checkedSeats,
  seatsAvailable,
  invitesCount
}) {
  return checkedSeats.some(seat => seatsAvailable[seat] < invitesCount);
}
/**
 * Returns the members that were invited by email and whose invitation did not fail. The invite
 * returns every member of the workspace or group, so they must never be taken as the ones invited.
 */
export function getInvitedMembers({
  members,
  emails,
  failedInvitations = []
}) {
  const failedEmails = new Set(failedInvitations.map(({
    identity
  }) => identity.toLowerCase()));
  const invitedEmails = new Set(emails.map(email => email.toLowerCase()).filter(email => !failedEmails.has(email)));
  return members.filter(({
    email
  }) => invitedEmails.has(email.toLowerCase()));
}